<template>
  <div>
    <vue-tel-input
      ref="telInput"
      :class="{ 'has-error': errorMessage, outlined }"
      :value="modelValue"
      :model-value="modelValue"
      auto-format
      @input="onInput"
      valid-characters-only
      :inputOptions="{ placeholder: translatedPlaceholder }"
      v-on:country-changed="validateInput"
      :defaultCountry="defaultCountry"
      mode="international"
      :style="style"
      @blur="validateInput"
      @validate="setIsValid"
      :preferred-countries="preferredCountries">
      <template #label v-if="required">
        {{ translatedLabel }}
        <span class="text-red">*</span>
      </template>
      <template v-slot:label v-if="tooltipText">
        <div class="with-tooltip">
          {{ translatedLabel }}
          <help-icon-tooltip iconStyleClass="grey darken-2">{{ tooltipText }}</help-icon-tooltip>
        </div>
      </template>
    </vue-tel-input>
    <span class="text--color-text-error" v-if="errorMessage">{{ errorMessage }}</span>
  </div>
</template>

<script>
import HelpIconTooltip from '@satellite/components/elements/HelpIconTooltip.vue';
import { DEFAULT_PHONE_COUNTRY, ISO3166FilteredCountryIso2Codes, t } from '@nova/core';

export default {
  components: {
    HelpIconTooltip
  },
  props: {
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    modelValue: {
      type: String,
      required: false,
      default: ''
    },
    validator: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    defaultCountry: {
      type: String,
      required: false,
      default: 'US'
    },
    marginTop: {
      type: String,
      required: false
    },
    tooltipText: {
      type: String,
      required: false,
      default: null
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false
    },
    placeholder: {
      type: String,
      required: false
    },
    outlined: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      errorMessage: '',
      isMounted: false,
      isValid: false
    };
  },
  computed: {
    translatedLabel() {
      return this.label ?? this.t('forms.elements.inputs.phoneField.labels.phoneNumber');
    },
    translatedPlaceholder() {
      return (
        this.placeholder ?? this.t('forms.elements.inputs.phoneField.placeholders.phoneNumber')
      );
    },
    preferredCountries() {
      return ISO3166FilteredCountryIso2Codes;
    },
    activeCountryIso2() {
      return this.$refs.telInput?.activeCountry?.iso2 ?? DEFAULT_PHONE_COUNTRY;
    },
    style() {
      if (this.marginTop) {
        return {
          'margin-top': this.marginTop
        };
      }
      return {};
    }
  },
  methods: {
    t,
    setIsValid(val) {
      this.isValid = val.valid;
    },
    validateInput() {
      this.errorMessage = '';
      if (this.isMounted) {
        if (!this.isValid) {
          this.errorMessage = this.t('global.errors.pleaseEnterAValidPhoneNumber');
        }
        if (this.required & !this.modelValue) {
          this.errorMessage = this.validator.rules.required(this.label)[0]();
        }
      }
    },
    onInput(val) {
      if (this.isMounted) {
        if (typeof val === 'object') {
          return;
        }
        if (val) {
          this.errorMessage = '';
        }
        this.$emit('update:model-value', val);
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.isMounted = true;
    });
  },
  watch: {
    defaultCountry(country) {
      if (!this.value) {
        this.$refs.telInput?.choose({ iso2: country });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.vue-tel-input {
  margin-top: 12px !important;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.3) !important;
  border-radius: 0;

  &:focus-within {
    box-shadow: none !important;
    border-bottom: 2px solid black !important;
  }

  &.has-error {
    border: 1px solid $color-text-error !important;
  }
}

.outlined {
  border: 1px solid rgba(0, 0, 0, 0.3) !important;
  border-radius: 4px;
}
</style>
